import { useEffect, useMemo, useState } from "react";
import api from "../api";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from "recharts";

import { motion } from "framer-motion";

export default function AuditDashboard() {

  const [logs, setLogs] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  useEffect(() => {
    fetchLogs();
  }, []);

  // =====================
  // FETCH
  // =====================

  const fetchLogs = async () => {

    try {

      const res =
        await api.get(
          "/users/audit"
        );

      setLogs(res.data);

    } catch (err) {

      console.error(err);

    } finally {

      setLoading(false);

    }
  };

  // =====================
  // DATA PAR JOUR
  // =====================

  const logsParJour =
    useMemo(() => {

      const map = {};

      logs.forEach((l) => {

        const d =
          new Date(
            l.created_at
          ).toLocaleDateString();

        map[d] =
          (map[d] || 0) + 1;

      });

      return Object.keys(map).map(
        (date) => ({
          date,
          total: map[date]
        })
      );

    }, [logs]);

  // =====================
  // DATA PAR ACTION
  // =====================

  const logsParAction =
    useMemo(() => {

      const map = {};

      logs.forEach((l) => {

        map[l.action] =
          (map[l.action] || 0) + 1;

      });

      return Object.keys(map).map(
        (action) => ({
          name: action,
          value: map[action]
        })
      );

    }, [logs]);

  const COLORS = [
    "#2563eb",
    "#16a34a",
    "#dc2626",
    "#f59e0b",
    "#6b7280"
  ];

  // =====================
  // LOADING
  // =====================

  if (loading) {

    return (
      <p className="p-4 md:p-6">
        Chargement...
      </p>
    );

  }

  // =====================
  // RENDER
  // =====================

  return (
    <div className="p-3 md:p-6 bg-gray-100 min-h-[100dvh] space-y-4 md:space-y-6">

      {/* HEADER */}
      <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
        Audit des actions
      </h1>

      {/* CHARTS */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">

        {/* BAR CHART */}
        <motion.div
          whileHover={{
            scale:
              window.innerWidth > 768
                ? 1.01
                : 1
          }}
          className="bg-white p-3 md:p-4 rounded-2xl shadow"
        >

          <h2 className="font-semibold text-gray-700 mb-3 text-base md:text-lg">
            Activité par jour
          </h2>

          <ResponsiveContainer
            width="100%"
            height={240}
          >

            <BarChart
              data={logsParJour}
            >

              <XAxis dataKey="date" />

              <YAxis />

              <Tooltip />

              <Bar dataKey="total" />

            </BarChart>

          </ResponsiveContainer>

        </motion.div>

        {/* PIE CHART */}
        <motion.div
          whileHover={{
            scale:
              window.innerWidth > 768
                ? 1.01
                : 1
          }}
          className="bg-white p-3 md:p-4 rounded-2xl shadow"
        >

          <h2 className="font-semibold text-gray-700 mb-3 text-base md:text-lg">
            Répartition des actions
          </h2>

          <div className="overflow-x-auto">

            <ResponsiveContainer
              width="100%"
              height={260}
            >

              <PieChart>

                <Pie
                  data={logsParAction}
                  dataKey="value"
                  nameKey="name"
                  outerRadius={80}
                >

                  {logsParAction.map(
                    (
                      entry,
                      index
                    ) => (
                      <Cell
                        key={index}
                        fill={
                          COLORS[
                            index %
                              COLORS.length
                          ]
                        }
                      />
                    )
                  )}

                </Pie>

                <Tooltip />

                <Legend />

              </PieChart>

            </ResponsiveContainer>

          </div>

        </motion.div>

      </div>

      {/* TABLE */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="bg-white p-3 md:p-6 rounded-2xl shadow"
      >

        <h2 className="text-base md:text-lg font-semibold text-gray-700 mb-4">
          Historique des actions
        </h2>

        <div className="overflow-x-auto">

          <table className="min-w-[700px] w-full text-sm">

            <thead className="bg-gray-100 text-gray-600">

              <tr>

                <th className="p-3 text-left">
                  Utilisateur
                </th>

                <th className="p-3 text-left">
                  Action
                </th>

                <th className="p-3 text-left">
                  Détails
                </th>

                <th className="p-3 text-left">
                  Date
                </th>

              </tr>

            </thead>

            <tbody>

              {logs.map((log) => (

                <tr
                  key={log.id}
                  className="border-t hover:bg-gray-50 transition"
                >

                  <td className="p-3 font-medium whitespace-nowrap">
                    {log.nom}
                  </td>

                  <td className="p-3 whitespace-nowrap">
                    {log.action}
                  </td>

                  <td className="p-3 text-gray-600 break-words max-w-[280px]">
                    {log.details}
                  </td>

                  <td className="p-3 whitespace-nowrap">
                    {new Date(
                      log.created_at
                    ).toLocaleString()}
                  </td>

                </tr>

              ))}

            </tbody>

          </table>

        </div>

      </motion.div>

    </div>
  );
}